import { DISPLAY_KEY_WHITESPACE, trimWhitespace } from './normalizationWhitespace';

// Display name for a profile object (post-migration docs carry `name`;
// mid-migration/legacy docs fall back to the name-shaped doc id).
export const profileName = (p) => p?.name || p?.id || '';

export const isDeletedProfile = (p) => p?.status === 'Deleted';
// Missing status ⇒ Active (docs that predate the migration).
export const isActiveProfile = (p) => p?.status !== 'Deleted';

// Firestore returns data_profiles in document-id order, which reads as arbitrary in the UI.
// Profile lists sort by display name instead — numerically aware, so "Profile 10" follows
// "Profile 2" rather than "Profile 1".
const nameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
export const byProfileName = (a, b) => nameCollator.compare(profileName(a), profileName(b));

// { profileId: displayName } over a full profile list (active + deleted).
export const buildProfileNameById = (profiles) =>
  Object.fromEntries((profiles || []).map((p) => [p.id, profileName(p)]));

// Firestore map fields carry no key-order guarantee — consecutive snapshots can deliver
// default_tags with keys shuffled. Anything iterating them for display must impose its
// own order: alphabetical by category.
export const sortedTagEntries = (tags) =>
  Object.entries(tags || {}).sort(([a], [b]) => nameCollator.compare(a, b));

// Uniqueness normalisation: lowercase, trim, '_' ≡ whitespace runs.
export const normalizeProfileName = (s) =>
  trimWhitespace(s || '', DISPLAY_KEY_WHITESPACE).toLowerCase()
    .replace(new RegExp(`[_${DISPLAY_KEY_WHITESPACE}]+`, 'g'), ' ');

// Among a table's profiles, the one that keeps `name` when more than one active
// profile carries it. A save is not atomic with its uniqueness check, so two
// sessions can save the same name in the same instant; every writer reads the
// list back afterwards and applies THIS rule, so all of them agree on the one
// winner: the earliest created (a profile with no created_at predates the
// field and counts as earliest), then the smaller document id. Null when no
// active profile has the name.
const createdAtMillis = (p) => {
  const value = p?.created_at;
  if (!value) return -Infinity;
  if (typeof value.toMillis === 'function') return value.toMillis();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? -Infinity : parsed;
};
export const profileNameWinner = (profiles, name) => {
  const key = normalizeProfileName(name);
  const holders = (profiles || [])
    .filter((p) => isActiveProfile(p) && normalizeProfileName(profileName(p)) === key);
  if (holders.length === 0) return null;
  return holders.reduce((best, p) => {
    const delta = createdAtMillis(p) - createdAtMillis(best);
    if (delta < 0) return p;
    if (delta === 0 && String(p.id) < String(best.id)) return p;
    return best;
  });
};
